/**
 * three.js construction for the Endless Palace: turns palaceLayout chunks into
 * meshes by dispatching to the chunk's room type (src/components/palace/
 * roomTypes), then dressing the result with everything common to all spaces —
 * wing plaques, gilt frame molding, and the linen canvas quads that
 * PalaceMuseum textures lazily.
 *
 * The kit (shared materials + unit geometries) is created per museum visit,
 * not per module load: GenerativeMuseum's teardown traverse-disposes the whole
 * scene, so anything module-level would come back dead on the second visit.
 * Everything in the kit is tagged `userData.shared` and skipped by
 * disposeChunk; chunk-owned resources are disposed explicitly.
 */

import * as THREE from 'three';
import { PALACE, mulberry32, hashString } from './constants';
import {
  box, angledWall, mergedMesh, wallForSide, casingsForNode, baseAndCornice,
  laylightCoffer, bench, drumWithDoors, standardShell,
} from './toolkit';
import { ROOM_TYPES } from './roomTypes';
import { FRAME_MATERIALS, frameStyleById } from './frameStyles';

const FRAME_DEPTH = 0.07; // fallback molding bar depth (off the wall)

// Salon wall grounds: one color per cluster, so the wall itself tells you which
// wing you're standing in. Hues walk the circle by the golden angle, which keeps
// consecutive wings far apart no matter how many clusters the archive has —
// a fixed palette wrapped around and made half the museum the same green.
const GOLDEN_TURN = 0.381966; // 1 - 1/phi
const salonColor = (i, pale) => {
  const h = (((0.04 + i * GOLDEN_TURN) % 1) + 1) % 1;
  // Ochres go muddy when they're dark and blues go icy when they're light, so
  // the warm half of the circle sits a little lighter and less saturated. Every
  // wing then reads at the same weight behind the gilt frames.
  const warm = Math.cos((h - 0.1) * Math.PI * 2) * 0.5 + 0.5; // 1 at ochre, 0 at prussian
  return pale
    ? new THREE.Color().setHSL(h, 0.15 + warm * 0.05, 0.85)
    : new THREE.Color().setHSL(h, 0.34 - warm * 0.08, 0.24 + warm * 0.06);
};

export const createPalaceKit = () => {
  const materials = {
    plaster: new THREE.MeshStandardMaterial({ color: 0xf2ede3, roughness: 0.9 }),
    trim: new THREE.MeshStandardMaterial({ color: 0xefeae0, roughness: 0.8 }),
    ceiling: new THREE.MeshStandardMaterial({ color: 0xf7f3ea, roughness: 0.9 }),
    benchWood: new THREE.MeshStandardMaterial({ color: 0x4a3427, roughness: 0.6 }),
    floor: new THREE.MeshStandardMaterial({ color: 0x8a6a4d, roughness: 0.75 }),
    marble: new THREE.MeshStandardMaterial({ color: 0xd8d2c6, roughness: 0.5 }),
    dome: new THREE.MeshStandardMaterial({ color: 0xefe8da, roughness: 0.85, side: THREE.DoubleSide }),
    gilt: new THREE.MeshStandardMaterial({ color: 0x9a7b2f, roughness: 0.45, metalness: 0.35 }),
    linen: new THREE.MeshStandardMaterial({ color: 0xf1ead9, roughness: 0.95 }),
    plaque: new THREE.MeshStandardMaterial({ color: 0x2e2a25, roughness: 0.6 }),
    laylight: new THREE.MeshStandardMaterial({
      color: 0xfff6e2,
      emissive: 0xfff0d4,
      emissiveIntensity: 0.9,
      side: THREE.DoubleSide,
    }),
    // The wider vocabulary for culturally-inspired types.
    stone: new THREE.MeshStandardMaterial({ color: 0x9b9486, roughness: 0.7 }),
    timber: new THREE.MeshStandardMaterial({ color: 0x5a4632, roughness: 0.65 }),
    water: new THREE.MeshStandardMaterial({
      color: 0x2e5c66,
      roughness: 0.15,
      metalness: 0.35,
      emissive: 0x102a30,
      emissiveIntensity: 0.35,
    }),
    terracotta: new THREE.MeshStandardMaterial({ color: 0x9c5b3f, roughness: 0.8 }),
  };

  // Frame mouldings, one material per surface class. They are deliberately
  // white: each style's timber or metal arrives as `instanceColor`, which the
  // shader multiplies in, so the catalogue's dozen colours cost nothing and
  // only roughness/metalness — the things that actually change the shading —
  // buy a separate draw call.
  Object.entries(FRAME_MATERIALS).forEach(([key, spec]) => {
    materials[key] = new THREE.MeshStandardMaterial({ color: 0xffffff, ...spec });
  });
  const geometries = {
    unitBox: new THREE.BoxGeometry(1, 1, 1),
    unitPlane: new THREE.PlaneGeometry(1, 1),
  };
  Object.values(materials).forEach((m) => { m.userData.shared = true; });
  Object.values(geometries).forEach((g) => { g.userData.shared = true; });

  // Salon wall materials are created lazily (a visit may only ever stream a
  // few wings) but shared across chunks, so they live and die with the kit.
  // Pass `pale` for the corridors and courts on the way into a wing: same hue,
  // washed out, so the route reads as an approach to that gallery.
  const wallCache = new Map();
  const wallFor = (i, pale = false) => {
    const idx = Math.abs(Math.round(i)) || 0;
    const key = `${idx}:${pale ? 1 : 0}`;
    if (!wallCache.has(key)) {
      const m = new THREE.MeshStandardMaterial({
        color: salonColor(idx, pale),
        roughness: pale ? 0.9 : 0.85,
      });
      m.userData.shared = true;
      wallCache.set(key, m);
    }
    return wallCache.get(key);
  };

  return { materials, geometries, wallFor, wallCache };
};

export const disposePalaceKit = (kit) => {
  Object.values(kit.materials).forEach((m) => m.dispose());
  Object.values(kit.geometries).forEach((g) => g.dispose());
  kit.wallCache.forEach((m) => m.dispose());
  kit.wallCache.clear();
};

// ---------------------------------------------------------------------------

const yAxis = new THREE.Vector3(0, 1, 0);

/**
 * The room's mouldings, one InstancedMesh per material bucket.
 *
 * Every save carries a style id chosen at plan time (see frameStyles.js), and a
 * style expands to a list of boxes. Those boxes are bucketed by material, not
 * by style: colour rides along as `instanceColor`, which three.js multiplies
 * into the material colour for free, so fourteen mouldings in a dozen timbers
 * and metals still cost four draw calls. Only roughness and metalness force a
 * bucket, because only those change the shader's inputs.
 *
 * Buckets are sized from the expanded bars rather than over-allocated: an
 * unwritten instance matrix is all zeroes, which decomposes to the world
 * origin, and one such instance stretches the mesh's bounding sphere from this
 * room to (0,0,0) and defeats frustum culling for the whole bucket.
 */
const buildFrames = (slots, kit) => {
  const buckets = new Map();
  slots.forEach((slot) => {
    const style = frameStyleById(slot.style);
    style.bars(slot.w, slot.h).forEach((bar) => {
      const key = bar.mat ?? style.mat;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push({ slot, bar, color: bar.color ?? style.color });
    });
  });

  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const pos = new THREE.Vector3();
  const scl = new THREE.Vector3();
  const tint = new THREE.Color();

  return [...buckets].map(([key, entries]) => {
    const mesh = new THREE.InstancedMesh(kit.geometries.unitBox, kit.materials[key], entries.length);
    entries.forEach(({ slot, bar, color }, i) => {
      q.setFromAxisAngle(yAxis, slot.rotY);
      // In the frame's own plane: (rx, rz) runs right, (nx, nz) is the wall
      // normal, so a bar's dz pushes it out toward the visitor.
      const rx = Math.cos(slot.rotY);
      const rz = -Math.sin(slot.rotY);
      const nx = Math.sin(slot.rotY);
      const nz = Math.cos(slot.rotY);
      const dz = bar.dz ?? 0;
      pos.set(
        slot.x + rx * bar.dx + nx * dz,
        slot.y + bar.dy,
        slot.z + rz * bar.dx + nz * dz,
      );
      scl.set(bar.sw, bar.sh, bar.sd ?? FRAME_DEPTH);
      m.compose(pos, q, scl);
      mesh.setMatrixAt(i, m);
      mesh.setColorAt(i, tint.setHex(color));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.computeBoundingSphere();
    // The visitor picks artwork, never moulding. Styles with a sight lip or a
    // mount lap over the canvas, and left pickable they would swallow the ray
    // and make that band of the picture quietly unclickable.
    mesh.raycast = () => {};
    return mesh;
  });
};

// Serif label on a dark ground, hung over a wing's door.
const plaqueTexture = (label) => {
  const canvas = document.createElement('canvas');
  canvas.width = 512;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#2e2a25';
  ctx.fillRect(0, 0, 512, 128);
  ctx.strokeStyle = '#9a7b2f';
  ctx.lineWidth = 3;
  ctx.strokeRect(8, 8, 496, 112);
  ctx.fillStyle = '#e8d9a8';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const text = String(label || '').toUpperCase();
  let px = 44;
  ctx.font = `${px}px Georgia, serif`;
  while (px > 18 && ctx.measureText(text).width > 460) {
    px -= 2;
    ctx.font = `${px}px Georgia, serif`;
  }
  ctx.fillText(text, 256, 66);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
};

// Interior face position + inward facing for a plaque on the given side.
const PLAQUE_FACING = {
  N: { rotY: Math.PI, fx: 0, fz: -1 },
  S: { rotY: 0, fx: 0, fz: 1 },
  E: { rotY: -Math.PI / 2, fx: -1, fz: 0 },
  W: { rotY: Math.PI / 2, fx: 1, fz: 0 },
};

const buildPlaques = (chunk, kit, group) => {
  const { WALL_T, DOOR_H } = PALACE;
  for (const spec of chunk.plaques || []) {
    const facing = PLAQUE_FACING[spec.side];
    const door = (chunk.doors || []).find((d) => d.side === spec.side && d.at === spec.at);
    const y = (door?.h || DOOR_H) + 0.55;
    const face = spec.side === 'N' ? chunk.z2 - WALL_T
      : spec.side === 'S' ? chunk.z1 + WALL_T
        : spec.side === 'E' ? chunk.x2 - WALL_T : chunk.x1 + WALL_T;
    const texture = plaqueTexture(spec.label);
    const material = new THREE.MeshStandardMaterial({ map: texture, roughness: 0.6 });
    const plaque = new THREE.Mesh(kit.geometries.unitPlane, material);
    plaque.scale.set(2.2, 0.55, 1);
    if (spec.side === 'N' || spec.side === 'S') {
      plaque.position.set(spec.at, y, face + facing.fz * 0.02);
    } else {
      plaque.position.set(face + facing.fx * 0.02, y, spec.at);
    }
    plaque.rotation.y = facing.rotY;
    plaque.matrixAutoUpdate = false;
    plaque.updateMatrix();
    group.add(plaque);
  }
};

// ---------------------------------------------------------------------------

export const buildChunk = (chunk, kit) => {
  const type = ROOM_TYPES[chunk.type];
  const T = {
    THREE,
    kit,
    PALACE,
    rand: mulberry32(hashString(chunk.id)),
    box,
    angledWall,
    mergedMesh,
    wallForSide,
    casingsForNode,
    baseAndCornice,
    laylightCoffer,
    bench,
    drumWithDoors,
    standardShell,
  };
  const { group, colliders } = type.build(chunk, T);

  buildPlaques(chunk, kit, group);

  if (chunk.slots.length) {
    buildFrames(chunk.slots, kit).forEach((mesh) => group.add(mesh));

    // Canvas quads: shared unit plane, scaled per slot, linen until the
    // texture queue reaches them. PalaceMuseum swaps materials in and out.
    chunk.slots.forEach((slot) => {
      const quad = new THREE.Mesh(kit.geometries.unitPlane, kit.materials.linen);
      quad.position.set(slot.x, slot.y, slot.z);
      quad.rotation.y = slot.rotY;
      quad.scale.set(slot.w, slot.h, 1);
      quad.matrixAutoUpdate = false;
      quad.updateMatrix();
      quad.userData.slot = slot;
      group.add(quad);
    });
  }

  group.userData.chunkId = chunk.id;
  group.userData.colliders = colliders || [];
  return group;
};

// Dispose everything a chunk owns; shared kit resources are left alone.
// Canvas-textured quad materials are handled by the caller's texture registry
// before this runs, but sweep defensively anyway.
export const disposeChunk = (group) => {
  group.traverse((node) => {
    if (node.geometry && !node.geometry.userData.shared) node.geometry.dispose();
    const materials = Array.isArray(node.material) ? node.material : node.material ? [node.material] : [];
    materials.forEach((material) => {
      if (material.userData.shared) return;
      if (material.map) material.map.dispose();
      material.dispose();
    });
    if (node.isInstancedMesh) node.dispose();
  });
  group.removeFromParent();
};
